class CemeHeader extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.megaItem = this.querySelector('[data-cn-mega-item]');
    this.megaTrigger = this.querySelector('[data-cn-mega-trigger]');
    this.overlay = this.querySelector('[data-cn-overlay]');
    this.drawer = this.querySelector('[data-cn-drawer]');
    this.drawerOpenButton = this.querySelector('[data-cn-drawer-open]');
    this.drawerCloseButton = this.querySelector('[data-cn-drawer-close]');
    this.accordion = this.querySelector('[data-cn-accordion]');
    this.accordionToggle = this.querySelector('[data-cn-accordion-toggle]');
    this.accordionPanel = this.querySelector('[data-cn-accordion-panel]');

    this.desktopQuery = window.matchMedia('(min-width: 990px)');
    this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.closeTimer = null;
    this.hoverOpenedAt = 0;

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onMegaEnter = this.onMegaEnter.bind(this);
    this.onMegaLeave = this.onMegaLeave.bind(this);
    this.onMegaFocusOut = this.onMegaFocusOut.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.openDrawer = this.openDrawer.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
    this.onAccordionClick = this.onAccordionClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);
    this.updateOffsets = this.updateOffsets.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onBlockDeselect = this.onBlockDeselect.bind(this);

    this.megaTrigger?.addEventListener('click', this.onTriggerClick);
    this.megaItem?.addEventListener('mouseenter', this.onMegaEnter);
    this.megaItem?.addEventListener('mouseleave', this.onMegaLeave);
    this.megaItem?.addEventListener('focusout', this.onMegaFocusOut);
    this.overlay?.addEventListener('click', this.onOverlayClick);
    this.drawerOpenButton?.addEventListener('click', this.openDrawer);
    this.drawerCloseButton?.addEventListener('click', this.closeDrawer);
    this.accordionToggle?.addEventListener('click', this.onAccordionClick);
    document.addEventListener('keydown', this.onKeydown);
    this.desktopQuery.addEventListener('change', this.onViewportChange);
    window.addEventListener('resize', this.updateOffsets);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    this.updateOffsets();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    this.desktopQuery.removeEventListener('change', this.onViewportChange);
    window.removeEventListener('resize', this.updateOffsets);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    window.clearTimeout(this.closeTimer);
    this.setBackgroundInert(false);
    document.documentElement.classList.remove('ceme-nav-lock');
  }

  updateOffsets() {
    let announceHeight = 0;
    this.section?.querySelectorAll('.ceme-announce').forEach((bar) => {
      announceHeight += bar.offsetHeight;
    });
    this.section?.style.setProperty('--cn-announce-h', `${announceHeight}px`);
    const barHeight = this.querySelector('.ceme-nav__bar')?.offsetHeight || 0;
    const root = document.documentElement.style;
    root.setProperty('--header-height', `${barHeight}px`);
    root.setProperty('--header-group-height', `${barHeight}px`);
  }

  openMega() {
    if (!this.megaItem) return;
    window.clearTimeout(this.closeTimer);
    this.megaItem.classList.add('is-open');
    this.megaTrigger.setAttribute('aria-expanded', 'true');
    this.classList.add('is-mega-open');
  }

  closeMega() {
    if (!this.megaItem) return;
    window.clearTimeout(this.closeTimer);
    this.megaItem.classList.remove('is-open');
    this.megaTrigger.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-mega-open');
  }

  isMegaOpen() {
    return this.megaItem?.classList.contains('is-open');
  }

  onTriggerClick() {
    if (this.isMegaOpen() && Date.now() - this.hoverOpenedAt > 400) {
      this.closeMega();
    } else {
      this.openMega();
    }
  }

  onMegaEnter() {
    if (!this.hoverQuery.matches || !this.desktopQuery.matches) return;
    window.clearTimeout(this.closeTimer);
    if (this.isMegaOpen()) return;
    this.closeTimer = window.setTimeout(() => {
      this.openMega();
      this.hoverOpenedAt = Date.now();
    }, 80);
  }

  onMegaLeave() {
    if (!this.hoverQuery.matches || !this.desktopQuery.matches) return;
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => this.closeMega(), 180);
  }

  onMegaFocusOut(event) {
    if (event.relatedTarget && !this.megaItem.contains(event.relatedTarget)) {
      this.closeMega();
    }
  }

  onOverlayClick() {
    this.closeMega();
    this.closeDrawer();
  }

  setBackgroundInert(state) {
    document.querySelectorAll('#MainContent, #shopify-section-ceme-footer, .shopify-section:not(#shopify-section-ceme-header)').forEach((node) => {
      if (state) {
        node.setAttribute('inert', '');
      } else {
        node.removeAttribute('inert');
      }
    });
  }

  focusables() {
    return [...this.drawer.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter(
      (node) => node.offsetParent !== null
    );
  }

  openDrawer() {
    if (!this.drawer) return;
    this.drawer.hidden = false;
    void this.drawer.offsetWidth;
    this.classList.add('is-drawer-open');
    this.drawerOpenButton?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('ceme-nav-lock');
    this.setBackgroundInert(true);
    window.setTimeout(() => this.drawerCloseButton?.focus(), 30);
  }

  closeDrawer() {
    if (!this.classList.contains('is-drawer-open')) return;
    this.classList.remove('is-drawer-open');
    this.drawerOpenButton?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('ceme-nav-lock');
    this.setBackgroundInert(false);
    const restoreFocus = this.drawer?.contains(document.activeElement);
    const finish = () => {
      if (!this.classList.contains('is-drawer-open')) this.drawer.hidden = true;
    };
    const duration = parseFloat(getComputedStyle(this.drawer).transitionDuration) || 0;
    if (duration > 0) {
      this.drawer.addEventListener('transitionend', finish, { once: true });
      window.setTimeout(finish, duration * 1000 + 50);
    } else {
      finish();
    }
    if (restoreFocus) this.drawerOpenButton?.focus();
  }

  setAccordion(open) {
    if (!this.accordion) return;
    this.accordion.classList.toggle('is-open', open);
    this.accordionToggle.setAttribute('aria-expanded', String(open));
    this.accordionPanel.hidden = !open;
  }

  onAccordionClick() {
    this.setAccordion(!this.accordion.classList.contains('is-open'));
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      if (this.isMegaOpen()) {
        this.closeMega();
        this.megaTrigger?.focus();
      }
      this.closeDrawer();
      return;
    }

    if (event.key !== 'Tab' || !this.classList.contains('is-drawer-open')) return;
    const items = this.focusables();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  onViewportChange() {
    this.closeMega();
    this.closeDrawer();
    this.updateOffsets();
  }

  onBlockSelect(event) {
    if (!this.contains(event.target)) return;
    const inMega = event.target.closest('.ceme-mega');
    if (this.desktopQuery.matches) {
      if (inMega) this.openMega();
    } else {
      this.openDrawer();
      if (inMega) this.setAccordion(true);
    }
  }

  onBlockDeselect(event) {
    if (!this.contains(event.target)) return;
    this.closeMega();
  }
}

if (!customElements.get('ceme-header')) {
  customElements.define('ceme-header', CemeHeader);
}
